import React, { useCallback, useRef } from 'react';

import { Card } from '@/components/ui/card';
import { cn } from '@/utils';

import { type ChartDownloadFormat, ChartDownloadMenu } from './ChartDownloadMenu';
import { InfoTooltip } from './InfoTooltip';

interface ChartCardProps {
  title: string;
  /** Muted line shown under the header, always visible (unlike `info`). */
  subtitle?: React.ReactNode;
  /** Help copy shown on hover. Accepts any React node. */
  info?: React.ReactNode;
  titleAccessory?: React.ReactNode;
  rightSlot?: React.ReactNode;
  onDownload?: (
    format: ChartDownloadFormat,
    exportElement?: HTMLElement | null,
  ) => void | Promise<void>;
  downloadDisabled?: boolean;
  className?: string;
  bodyClassName?: string;
  exportSectionId?: string;
  children: React.ReactNode;
}

/**
 * Shell for every chart in the analytics surface. Provides a consistent header
 * (title + info tooltip + optional right slot + download menu), an optional
 * subtitle line and body padding.
 *
 * Download handler is wired here so callsites only need to pass the rowset
 * shape; the actual export logic lives in Phase 6's useDownloadChart hook.
 */
export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  info,
  titleAccessory,
  rightSlot,
  onDownload,
  downloadDisabled,
  className,
  bodyClassName,
  exportSectionId,
  children,
}) => {
  const exportRef = useRef<HTMLDivElement>(null);
  const handleDownload = useCallback(
    (format: ChartDownloadFormat) => onDownload?.(format, exportRef.current),
    [onDownload],
  );

  return (
    <Card
      ref={exportRef}
      className={cn('flex flex-col shadow-sm', className)}
      data-export-pdf-block
      data-export-section={exportSectionId}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4 pb-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 className="text-sm font-medium text-foreground">{title}</h3>
            {titleAccessory}
            {info && (
              <span data-export-hidden>
                <InfoTooltip content={info} />
              </span>
            )}
          </div>
          {info && (
            <div
              data-export-visible="block"
              className="hidden max-w-3xl text-xs leading-relaxed text-muted-foreground"
            >
              {info}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2" data-export-hidden>
          {rightSlot}
          {onDownload && (
            <ChartDownloadMenu onDownload={handleDownload} disabled={downloadDisabled} />
          )}
        </div>
      </header>
      {subtitle && <p className="px-4 pb-1 text-xs leading-5 text-muted-foreground">{subtitle}</p>}
      <div className={cn('px-4 pb-4 pt-2', bodyClassName)}>{children}</div>
    </Card>
  );
};
